import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getReferralStats, redeemReferralCredit } from '@/services/api';
import { toast } from 'sonner';
import { Gift, Users, Copy, Check, Star, Clock, Share2, Sparkles } from 'lucide-react';

export default function ReferralsPage() {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await getReferralStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${stats?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const handleRedeemCredit = async () => {
    if (!stats || stats.credits_earned < 1) {
      toast.error('No credits available to redeem');
      return;
    }
    
    setRedeeming(true);
    try {
      await redeemReferralCredit();
      toast.success('1 week added to your subscription!');
      await fetchStats();
      await refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to redeem credit';
      toast.error(message);
    } finally {
      setRedeeming(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center text-stone-500">Loading referral data...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="referrals-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#5F8D4E]/10 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-[#5F8D4E]" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
            Refer & Earn
          </h1>
          <p className="text-stone-600 max-w-lg mx-auto">
            Share StyleReview with fellow hair professionals and earn free subscription time when they join!
          </p>
        </div>

        {/* Referral Code Card */}
        <Card className="border-2 border-[#5F8D4E] bg-[#5F8D4E]/5 mb-8" data-testid="referral-code-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#5F8D4E] mb-1">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-stone-900 tracking-wider">
                    {stats?.referral_code || '—'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyReferralCode}
                    className="h-9 px-3 rounded-full border-[#5F8D4E] text-[#5F8D4E] hover:bg-[#5F8D4E]/10"
                    data-testid="copy-code-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <Button
                onClick={copyReferralLink}
                className="h-11 px-6 rounded-full bg-[#5F8D4E] text-white hover:bg-[#4A7340]"
                data-testid="share-link-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Link
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="border-stone-100 shadow-sm" data-testid="stat-total-referrals">
            <CardContent className="p-5 text-center">
              <Users className="w-8 h-8 text-stone-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-stone-900">{stats?.total_referrals || 0}</p>
              <p className="text-sm text-stone-600">Total Referrals</p>
            </CardContent>
          </Card>
          <Card className="border-stone-100 shadow-sm" data-testid="stat-successful">
            <CardContent className="p-5 text-center">
              <Star className="w-8 h-8 text-[#D97757] mx-auto mb-2" />
              <p className="text-3xl font-bold text-stone-900">{stats?.successful_referrals || 0}</p>
              <p className="text-sm text-stone-600">Successful (Subscribed)</p>
            </CardContent>
          </Card>
          <Card className="border-stone-100 shadow-sm" data-testid="stat-credits">
            <CardContent className="p-5 text-center">
              <Sparkles className="w-8 h-8 text-[#5F8D4E] mx-auto mb-2" />
              <p className="text-3xl font-bold text-stone-900">{stats?.credits_earned || 0}</p>
              <p className="text-sm text-stone-600">Credits Available</p>
            </CardContent>
          </Card>
        </div>

        {/* Redeem Credits */}
        {stats?.credits_earned > 0 && (
          <Card className="border-stone-100 shadow-sm mb-8" data-testid="redeem-card">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="font-medium text-stone-900 mb-1">Redeem Your Credits</h3>
                  <p className="text-sm text-stone-600">
                    You have {stats.credits_earned} credit{stats.credits_earned !== 1 ? 's' : ''} (1 credit = 1 week free)
                  </p>
                </div>
                <Button
                  onClick={handleRedeemCredit}
                  disabled={redeeming}
                  className="h-11 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]"
                  data-testid="redeem-btn"
                >
                  {redeeming ? 'Redeeming...' : 'Redeem 1 Week'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How It Works */}
        <Card className="border-stone-100 shadow-sm mb-8" data-testid="how-it-works-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  step: '1',
                  title: 'Share Your Code',
                  description: 'Send your referral code or link to fellow hair professionals'
                },
                {
                  step: '2',
                  title: 'They Subscribe',
                  description: 'When they sign up and subscribe, you earn a credit'
                },
                {
                  step: '3',
                  title: 'Get Free Time',
                  description: 'Redeem credits for free subscription weeks anytime'
                }
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#5F8D4E]/10 flex items-center justify-center mx-auto mb-3">
                    <span className="font-bold text-[#5F8D4E]">{item.step}</span>
                  </div>
                  <h4 className="font-medium text-stone-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-stone-600">{item.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Referral History */}
        <Card className="border-stone-100 shadow-sm" data-testid="referral-history-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">Referral History</CardTitle>
          </CardHeader>
          <CardContent>
            {!stats?.referral_history || stats.referral_history.length === 0 ? (
              <div className="py-8 text-center">
                <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No referrals yet</p>
                <p className="text-sm text-stone-400 mt-2">
                  Start sharing your code to see your referrals here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {stats.referral_history.map((referral) => (
                  <div 
                    key={referral.id}
                    className="py-4 flex items-center justify-between"
                    data-testid={`referral-${referral.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                        <span className="text-stone-600 font-medium">
                          {referral.referred_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{referral.referred_name}</p>
                        <p className="text-xs text-stone-500">
                          Joined {formatDate(referral.created_at)}
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      referral.status === 'completed' 
                        ? 'bg-[#5F8D4E]/10 text-[#5F8D4E]' 
                        : 'bg-amber-100 text-amber-700'
                    }>
                      {referral.status === 'completed' ? (
                        <><Check className="w-3 h-3 mr-1" /> Subscribed</>
                      ) : (
                        <><Clock className="w-3 h-3 mr-1" /> Pending</>
                      )}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
